import { Stack, Tooltip } from '@mui/material';
import classNames from 'classnames';
import { useState, type PropsWithChildren } from 'react';

import { ButtonWithIcon } from './buttonWithIcon';

import { ExtendedIconButton } from '../shared/extendedIconButton';

interface CopyToClipboardProps {
  buttonLabel?: string;
  textToCopy?: string;
  tooltip?: string;
  disabledTooltip?: string;
  className?: string;
  size?: 'small' | 'medium';
  showOnlyOnHover?: boolean;
}

export function CopyToClipboardButton(props: CopyToClipboardProps) {
  const {
    textToCopy,
    tooltip = 'Copy',
    disabledTooltip = tooltip,
    buttonLabel,
    showOnlyOnHover = false,
    size = 'medium',
  } = props;
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!textToCopy) return;

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000); // Reset the tooltip after 2 seconds
      })
      .catch(() => {
        setCopySuccess(false);
      });
  };

  return buttonLabel ? (
    <Tooltip
      title={
        copySuccess ? (
          <Stack direction='row' alignItems='center' gap='4px'>
            <i className='material-symbols-check text-[18px]' /> Copied
          </Stack>
        ) : undefined
      }
    >
      <ButtonWithIcon
        variant='outlined'
        text={buttonLabel}
        onClick={handleCopy}
        disabled={!textToCopy}
        className={classNames({
          'opacity-0 group-hover/copy:opacity-100 transition-opacity':
            showOnlyOnHover,
          'min-w-[24px] min-h-[24px] w-[24px] h-[24px]': size === 'small',
        })}
        icon={`material-symbols-content-copy-outline ${props.className ?? ''}`}
      />
    </Tooltip>
  ) : (
    <ExtendedIconButton
      tooltip={
        copySuccess ? (
          <Stack direction='row' alignItems='center' gap='4px'>
            <i className='material-symbols-check text-[18px]' /> Copied
          </Stack>
        ) : !textToCopy ? (
          disabledTooltip
        ) : (
          tooltip
        )
      }
      aria-label={tooltip}
      onClick={handleCopy}
      className={classNames({
        'opacity-0 group-hover/copy:opacity-100 transition-opacity':
          showOnlyOnHover,
        'min-w-[24px] min-h-[24px] w-[24px] h-[24px]': size === 'small',
      })}
      disabled={!textToCopy}
    >
      <i
        className={classNames(
          'material-symbols-content-copy-outline p-[2px]',
          { 'w-[16px] h-[16px] min-w-[16px] min-h-[16px]': size === 'small' },
          { 'w-[20px] h-[20px]': size === 'medium' },
          props.className
        )}
      />
    </ExtendedIconButton>
  );
}

export const copyToClipboardGroupKey = 'group/copy';

export const CopyToClipboardWrapper = ({
  children,
  gap = '4px',
  textToCopy,
  ...rest
}: PropsWithChildren<
  { textToCopy: string; gap?: string } & CopyToClipboardProps
>) => {
  if (!textToCopy) return children;

  return (
    <div className={`${copyToClipboardGroupKey} overflow-hidden`}>
      <Stack direction='row' alignItems='center' gap={gap}>
        {children}
        <CopyToClipboardButton
          showOnlyOnHover
          size='small'
          textToCopy={textToCopy}
          {...rest}
        />
      </Stack>
    </div>
  );
};
